import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  AlertTriangle, Shield, CreditCard, Users, ChevronDown, ChevronUp,
  Settings2, Save, RefreshCw, UserX, TrendingDown, Eye
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const RiskBadge = ({ level, score }) => {
  const styles = {
    high: "bg-[#FDE8E8] text-rose-600 border-[#F5C6C6]",
    medium: "bg-[#FEF3CD] text-[#856404] border-[#FCEABB]",
    low: "bg-[#D4EDDA] text-[#155724] border-[#C3E6CB]",
  };
  return (
    <span data-testid={`risk-badge-${level}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${styles[level] || styles.low}`}>
      {level === "high" && <AlertTriangle className="w-3 h-3" />}
      {level === "medium" && <TrendingDown className="w-3 h-3" />}
      {level === "low" && <Shield className="w-3 h-3" />}
      {level.charAt(0).toUpperCase() + level.slice(1)} ({score})
    </span>
  );
};

export default function NoShowRiskPage() {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [editSettings, setEditSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [riskRes, settingsRes] = await Promise.all([
        api.get("/noshow-risk"),
        api.get("/noshow-risk/settings"),
      ]);
      setData(riskRes.data);
      setSettings(settingsRes.data);
      setEditSettings(settingsRes.data);
    } catch (e) {
      toast.error("Failed to load risk data");
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const toggleDeposit = async (clientId, current) => {
    try {
      await api.put(`/clients/${clientId}/deposit-required`, { deposit_required: !current });
      toast.success(!current ? "Deposit required" : "Deposit removed");
      fetchData();
    } catch {
      toast.error("Failed to update");
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/noshow-risk/settings", editSettings);
      toast.success("Risk settings saved");
      fetchData();
      setShowSettings(false);
    } catch {
      toast.error("Failed to save settings");
    }
    setSaving(false);
  };

  const filteredClients = data?.clients?.filter(c => {
    if (filter === "all") return true;
    return c.risk_level === filter;
  }) || [];

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-5 h-5 animate-spin text-rose-400" />
      </div>
    );
  }

  const summary = data?.summary || {};

  return (
    <div data-testid="noshow-risk-page" className="p-6 lg:p-8 max-w-[1400px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight">No-Show Risk Intelligence</h1>
          <p className="text-sm text-slate-400 mt-1">AI-powered prediction and deposit enforcement to protect your revenue</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSettings(!showSettings)}
            className="border-slate-200 text-slate-600"
            data-testid="risk-settings-btn"
          >
            <Settings2 className="w-4 h-4 mr-1.5" /> Settings
          </Button>
          <Button size="sm" onClick={fetchData} className="bg-slate-900 text-white" data-testid="risk-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div data-testid="risk-settings-panel" className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
          <h3 className="font-['Outfit'] font-medium text-slate-900">Risk Thresholds & Deposit Settings</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">High Risk Threshold</label>
              <Input
                type="number" min={0} max={100}
                value={editSettings.high_risk_threshold || 70}
                onChange={e => setEditSettings(p => ({ ...p, high_risk_threshold: +e.target.value }))}
                className="border-slate-200"
                data-testid="high-threshold-input"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Medium Risk Threshold</label>
              <Input
                type="number" min={0} max={100}
                value={editSettings.medium_risk_threshold || 40}
                onChange={e => setEditSettings(p => ({ ...p, medium_risk_threshold: +e.target.value }))}
                className="border-slate-200"
                data-testid="medium-threshold-input"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Deposit Amount ($)</label>
              <Input
                type="number" min={0}
                value={editSettings.deposit_amount || 25}
                onChange={e => setEditSettings(p => ({ ...p, deposit_amount: +e.target.value }))}
                className="border-slate-200"
                data-testid="deposit-amount-input"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Lookback Period (days)</label>
              <Input
                type="number" min={30}
                value={editSettings.lookback_days || 180}
                onChange={e => setEditSettings(p => ({ ...p, lookback_days: +e.target.value }))}
                className="border-slate-200"
                data-testid="lookback-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={editSettings.auto_flag_high_risk || false}
              onCheckedChange={v => setEditSettings(p => ({ ...p, auto_flag_high_risk: v }))}
              data-testid="auto-flag-switch"
            />
            <span className="text-sm text-slate-600">Automatically require deposits for high-risk clients</span>
          </div>
          <div className="flex gap-2 pt-2">
            <Button size="sm" onClick={saveSettings} disabled={saving} className="bg-slate-900 text-white" data-testid="save-settings-btn">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save Settings"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowSettings(false)} className="border-slate-200">Cancel</Button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div data-testid="stat-total-flagged" className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#FAFAFA] flex items-center justify-center">
              <Users className="w-5 h-5 text-slate-600" strokeWidth={1.5} />
            </div>
          </div>
          <p className="font-['Outfit'] text-2xl font-medium text-slate-900">{summary.total_flagged || 0}</p>
          <p className="text-sm text-slate-400 mt-0.5">Flagged Clients</p>
        </div>
        <div data-testid="stat-high-risk" className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#FDE8E8] flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-rose-600" strokeWidth={1.5} />
            </div>
          </div>
          <p className="font-['Outfit'] text-2xl font-medium text-rose-600">{summary.high_risk || 0}</p>
          <p className="text-sm text-slate-400 mt-0.5">High Risk</p>
        </div>
        <div data-testid="stat-medium-risk" className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#FEF3CD] flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-[#856404]" strokeWidth={1.5} />
            </div>
          </div>
          <p className="font-['Outfit'] text-2xl font-medium text-[#856404]">{summary.medium_risk || 0}</p>
          <p className="text-sm text-slate-400 mt-0.5">Medium Risk</p>
        </div>
        <div data-testid="stat-deposits" className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#D4EDDA] flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-[#155724]" strokeWidth={1.5} />
            </div>
          </div>
          <p className="font-['Outfit'] text-2xl font-medium text-[#155724]">{summary.deposits_enforced || 0}</p>
          <p className="text-sm text-slate-400 mt-0.5">Deposits Enforced</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: "all", label: "All Flagged", count: summary.total_flagged },
          { key: "high", label: "High Risk", count: summary.high_risk },
          { key: "medium", label: "Medium", count: summary.medium_risk },
          { key: "low", label: "Low", count: summary.low_risk },
        ].map(tab => (
          <button
            key={tab.key}
            data-testid={`filter-${tab.key}`}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === tab.key
                ? "bg-slate-900 text-white"
                : "bg-white border border-slate-200 text-slate-600 hover:border-rose-300"
            }`}
          >
            {tab.label} ({tab.count || 0})
          </button>
        ))}
      </div>

      {/* Risk Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-[#FAFAF9]">
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">Client</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">Risk Score</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">No-Shows</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">Rate</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">Cancels</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">Card on File</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">Deposit Required</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-slate-400">
                    <Shield className="w-10 h-10 mx-auto mb-2 text-[#D1D1D1]" />
                    <p className="font-medium">No clients match this filter</p>
                  </td>
                </tr>
              ) : (
                filteredClients.map(client => (
                  <tr key={client.client_id} data-testid={`risk-row-${client.client_id}`} className="border-b border-slate-100 hover:bg-[#FAFAF9] transition-colors">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{client.client_name}</p>
                        <p className="text-xs text-slate-400">{client.email}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <RiskBadge level={client.risk_level} score={client.risk_score} />
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="font-semibold text-slate-900">{client.noshow_count}</span>
                      <span className="text-xs text-slate-400 ml-1">/ {client.total_appointments}</span>
                    </td>
                    <td className="py-3 px-4 text-center text-sm text-slate-600">{client.noshow_rate}%</td>
                    <td className="py-3 px-4 text-center text-sm text-slate-600">{client.cancel_count}</td>
                    <td className="py-3 px-4 text-center">
                      {client.has_card_on_file ? (
                        <span className="inline-flex items-center gap-1 text-xs text-[#155724] bg-[#D4EDDA] px-2 py-0.5 rounded-full">
                          <CreditCard className="w-3 h-3" /> Yes
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">None</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Switch
                        checked={client.deposit_required}
                        onCheckedChange={() => toggleDeposit(client.client_id, client.deposit_required)}
                        data-testid={`deposit-switch-${client.client_id}`}
                      />
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/clients/${client.client_id}`)}
                        className="text-slate-600 hover:text-slate-900"
                        data-testid={`view-client-${client.client_id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-[#FAFAFA] rounded-2xl p-4 text-xs text-slate-400 space-y-1">
        <p className="font-medium text-slate-600 mb-1">How Risk Scores Work</p>
        <p>Scores are calculated using: recent no-show rate (40%), overall no-show rate (25%), cancellation rate (15%), and total no-show count (20%).</p>
        <p>Enable "Deposit Required" to enforce a ${data?.settings?.deposit_amount || 25} hold when high-risk clients book. Clients with a card on file can be charged automatically.</p>
      </div>
    </div>
  );
}
